import { optional, string, zodLess, ZodLessInfer, ZodLessInferInput, zrecord } from '@kaiizen/zod-less';
import { getUniqueId } from '../commonLib';
import {
  ScanTemplateGroup,
  ScanTemplateLineItem,
  ScanTemplateOption,
  ScanTemplatePartSchema,
  ScanTemplateRoot,
  ScanTemplateSkuOption,
  ScanTemplateTask,
  ScanTemplateValues,
  ScanTemplateValueSchema,
} from '../ScanTemplateRoot';

export const StandardScanTemplateValueSchema = ScanTemplateValueSchema.omit({
  description: true,
});

export const StandardScanTemplatePartSchema = ScanTemplatePartSchema.omit({
  values: true,
}).extend({
  values: optional(zrecord(StandardScanTemplateValueSchema)),
});

export const StandardScanTemplateLineItemSchema = StandardScanTemplatePartSchema.omit({
  // values - only allowed on Groups, Tasks and Line Items
  trade: true,
  subtype: true,
  // skuIdOptions - only allowed on Line Items
  // useScopeStageSkus - only allowed on Line Items
});

export const StandardScanTemplateOptionSchema = StandardScanTemplatePartSchema.extend({
  lineItemTemplates: optional([StandardScanTemplateLineItemSchema]),
}).omit({
  trade: true,
  values: true,
  subtype: true,
  skuIdOptions: true,
  useScopeStageSkus: true, // useScopeStageSkus - only allowed on Line Items
});

export const StandardTaskSubtypeSchema = zodLess('otherTask');

export const StandardTaskTradeSchema = zodLess(
  'general|carpenter|electrician|plumber|hvac|roofer|mason|flooring|tiler|painting|insulation|glazier|concrete|steel|excavation|landscaper|pestControl|septic|structuralEngineer|cleaner|countertops|cabinets|windows|garageDoor|poolVendor|inHouse|appliances|foundation|locksmithRekey|resurfacing',
);
// pool? drywall? countertop?

export const StandardScanTemplateTaskSchema = StandardScanTemplatePartSchema.extend({
  optionTemplates: optional([StandardScanTemplateOptionSchema]),
}).omit({
  // trade - only allowed on Tasks
  // values - only allowed on Groups, Tasks and Line Items
  subtype: true,
  skuIdOptions: true,
  useScopeStageSkus: true, // useScopeStageSkus - only allowed on Line Items
}).extend({
  subtype: optional(StandardTaskSubtypeSchema),
  trade: optional(StandardTaskTradeSchema),
});

export const StandardGroupSubtypeSchema = zodLess('mainBedroom|otherBedroom|mainBathroom|otherBathroom|otherRoom');
export const StandardGroupRoomSubtypeSchema = StandardGroupSubtypeSchema; // all defined subtypes are rooms -- so far

export const STANDARD_GROUP_SUBTYPES = StandardGroupSubtypeSchema.options;
export const isStandardGroupSubtype = (subtype: string | undefined): subtype is StandardGroupSubtype => STANDARD_GROUP_SUBTYPES.includes(subtype as any);

export const StandardScanTemplateGroupSchema = StandardScanTemplatePartSchema.extend({
  taskTemplateIds: optional([string]), // Normalized data ONLY
}).omit({
  trade: true,
  // values - only allowed on Groups, Tasks and Line Items
  subtype: true,
  skuIdOptions: true,
  useScopeStageSkus: true,
}).extend({
  subtype: optional(StandardGroupSubtypeSchema),
});

export const StandardScanTemplateRootSchema = StandardScanTemplatePartSchema.extend({
  groupTemplates: optional([StandardScanTemplateGroupSchema]),
  taskTemplatesById: zrecord(StandardScanTemplateTaskSchema).optional(), // Normalized data: template-wide map of task template ID to task template allowing tasks to be shared across groups
}).omit({
  trade: true,
  values: true,
  subtype: true,
  skuIdOptions: true,
  useScopeStageSkus: true,
});

export type StandardTaskSubtype = ZodLessInfer<typeof StandardTaskSubtypeSchema>;
export type StandardGroupSubtype = ZodLessInfer<typeof StandardGroupSubtypeSchema>;
export type StandardTaskTrade = ZodLessInfer<typeof StandardTaskTradeSchema>;
export type StandardScanTemplateLineItem = ZodLessInfer<typeof StandardScanTemplateLineItemSchema>;
export type StandardScanTemplateOption = ZodLessInfer<typeof StandardScanTemplateOptionSchema>;
export type StandardScanTemplateTask = ZodLessInfer<typeof StandardScanTemplateTaskSchema>;
export type StandardScanTemplateGroup = ZodLessInfer<typeof StandardScanTemplateGroupSchema>;
export type StandardScanTemplateRoot = ZodLessInfer<typeof StandardScanTemplateRootSchema>;

export const isTaskSubtype = (subtype: string | undefined): boolean => StandardTaskSubtypeSchema.safeParse(subtype).success;
export const isGroupSubtype = (subtype: string | undefined): boolean => StandardGroupSubtypeSchema.safeParse(subtype).success;
export const isGroupRoomSubtype = (subtype: string | undefined): boolean => StandardGroupRoomSubtypeSchema.safeParse(subtype).success;

// **************************************************************
// Standard Scan Template Input Types
// **************************************************************
// Input types are slightly less restrictive because defaults can be applied for missing fields
// These are helpful when defining inline template data in TypeScript (like when vibe-converting templates)
export type StandardScanTemplateLineItemInput = ZodLessInferInput<typeof StandardScanTemplateLineItemSchema>;
export type StandardScanTemplateOptionInput = ZodLessInferInput<typeof StandardScanTemplateOptionSchema>;
export type StandardScanTemplateTaskInput = ZodLessInferInput<typeof StandardScanTemplateTaskSchema>;
export type StandardScanTemplateGroupInput = ZodLessInferInput<typeof StandardScanTemplateGroupSchema>;
export type StandardScanTemplateRootInput = ZodLessInferInput<typeof StandardScanTemplateRootSchema>;

/**
 * Takes any ScanTemplatePart and assigns IDs to it and its nested parts.
 * TODO: Templatize this to ensure that the input and output types match.
 * @param part The ScanTemplatePart to process.
 * @return A copy of the ScanTemplatePart with IDs assigned.
 */
export const assignKaiObjectIds = (
  kaiPart:
    | ScanTemplateRoot
    | ScanTemplateGroup
    | ScanTemplateGroup[]
    | ScanTemplateTask
    | ScanTemplateTask[]
    | ScanTemplateOption
    | ScanTemplateOption[]
    | ScanTemplateLineItem
    | ScanTemplateLineItem[]
    | ScanTemplateSkuOption
    | ScanTemplateSkuOption[],
):
  | ScanTemplateRoot
  | ScanTemplateGroup
  | ScanTemplateGroup[]
  | ScanTemplateTask
  | ScanTemplateTask[]
  | ScanTemplateOption
  | ScanTemplateOption[]
  | ScanTemplateLineItem
  | ScanTemplateLineItem[]
  | ScanTemplateSkuOption
  | ScanTemplateSkuOption[] =>
{
  if (typeof kaiPart !== 'object' || kaiPart === null || kaiPart === undefined) {
    return kaiPart;
  }

  // Deep clone the input to avoid mutating it.
  kaiPart = JSON.parse(JSON.stringify(kaiPart));

  const _fnAssignIdsInPlace = (kp: any) => {
    if (Array.isArray(kp)) {
      for (const kpelem of kp) {
        _fnAssignIdsInPlace(kpelem);
      }
      return;
    }

    // If kp is not an object, or if it's null or undefined,
    // then early out.
    if (typeof kp !== 'object' || kp === null || kp === undefined) {
      return;
    }

    if (!kp.id) {
      kp.id = getUniqueId();
    }

    // If it's a Line Item, recurse into its SKU ID options.
    if ('skuIdOptions' in kp && Array.isArray(kp.skuIdOptions)) {
      _fnAssignIdsInPlace(kp.skuIdOptions);
    }

    // If it's an Option, recurse into its line items.
    if ('lineItemTemplates' in kp && Array.isArray(kp.lineItemTemplates)) {
      _fnAssignIdsInPlace(kp.lineItemTemplates);
    }

    // If it's a Task, recurse into its options.
    if ('optionTemplates' in kp && Array.isArray(kp.optionTemplates)) {
      _fnAssignIdsInPlace(kp.optionTemplates);
    }

    // If it's a Group, nothing more to do.
    // (Comment left here for sake of completeness.)

    // If it's the Root, recurse into its tasks and groups.
    if ('taskTemplatesById' in kp && kp.taskTemplatesById) {
      const tasks = [...Object.values(kp.taskTemplatesById)];
      _fnAssignIdsInPlace(tasks);
    }

    if ('groupTemplates' in kp && Array.isArray(kp.groupTemplates)) {
      _fnAssignIdsInPlace(kp.groupTemplates);
    }
  };

  _fnAssignIdsInPlace(kaiPart);
  return kaiPart;
};

/**
 * The line-item value names the standard UX displays and edits (the shapes
 * getStandardLineItem generates). Line items may have any number of additional
 * values; they participate in calculations and produce correct results, but
 * the standard line-item UX only lists them as non-standard.
 */
export const STANDARD_LINE_ITEM_VALUE_NAMES: string[] = [
  'quantity',
  'price',
  'materialCost',
  'laborPrice',
  'laborCost',
  'totalCost',
  'measurement',
  'overage',
  'coverage',
];

/**
 * Standard set of values for room groups.
 */
export const STANDARD_ROOM_GROUP_VALUES = {
  roomLength: { type: 'ft' },
  roomWidth: { type: 'ft' },
  roomHeight: { type: 'ft' },
  roomArea: { type: 'sqft', value: 'roomLength * roomWidth' },
  roomPerimeter: { type: 'ft', value: '2 * (roomLength + roomWidth)' },
  roomWallArea: { type: 'sqft', value: 'roomPerimeter * roomHeight' },
  // Measured from the room's floor plan; until a room has one they fall back to the plain-rectangle estimate or stay empty.
  roomBaseboardLength: { type: 'ft', value: 'roomPerimeter' },
  roomCrownLength: { type: 'ft', value: 'roomPerimeter' },
  roomDoorTrimLength: { type: 'ft' },
  roomWindowTrimLength: { type: 'ft' },
  roomWindowSillLength: { type: 'ft' },
  roomCountertopArea: { type: 'sqft' },
} as const satisfies ScanTemplateValues;

/**
 * Returns a standard set of bedroom and bathroom groups for use in Kai Templates.
 * Note that a valid standard template is guaranteed to always have at least one
 * bedroom group marked "otherBedroom", and at least one bathroom group marked
 * "otherBathroom". *Most* standard templates will also have "primaryBedroom" and
 * "primaryBathroom" groups, but these are optional; a workflow that doesn't
 * differentiate between the primary bedroom/bathroom and all others will omit
 * primary groups and just have "otherBedroom" and "otherBathroom" groups for all
 * bedrooms and bathrooms, respectively.
 * @param omitMasters If true, the returned array will omit primary bedroom and primary
 *     bathroom groups. This is helpful for templates that are only meant to be used
 *     in workflows that don't differentiate between the primary bedroom/bathroom and
 *     all others.
 * @returns An array of ScanTemplateGroup objects for bedrooms and bathrooms.
 */
export const standardBedroomBathroomGroups = (omitMasters?: boolean): ScanTemplateGroup[] => {
  const retval = [] as ScanTemplateGroup[];

  if (!omitMasters) {
    retval.push({
      name: 'Primary Bedroom',
      description: 'Primary Bedroom / Main Bedroom',
      id: getUniqueId(),
      category: 'interior',
      subtype: 'mainBedroom',
      values: STANDARD_ROOM_GROUP_VALUES,
      taskTemplateIds: [],
    } as ScanTemplateGroup);

    retval.push({
      name: 'Primary Bathroom',
      description: 'Primary Bathroom / Main Bathroom',
      id: getUniqueId(),
      category: 'interior',
      subtype: 'mainBathroom',
      values: STANDARD_ROOM_GROUP_VALUES,
      taskTemplateIds: [],
    } as ScanTemplateGroup);
  }

  retval.push({
    name: 'Bedroom',
    description: `Other Bedroom(s), e.g. Guest Bedroom, Kids Bedroom, `
      + `Secondary Bedroom, Bathroom General, etc.`,
    id: 'group--default--other-bedroom',
    category: 'interior',
    subtype: 'otherBedroom',
    values: STANDARD_ROOM_GROUP_VALUES,
    taskTemplateIds: [],
  } as ScanTemplateGroup);

  retval.push({
    name: 'Bathroom',
    description: `Other Bathroom(s), e.g. Guest Bathroom, Kids Bathroom, `
      + `Secondary Bathroom, Bathroom General, etc.`,
    id: 'group--default--other-bathroom',
    category: 'interior',
    subtype: 'otherBathroom',
    values: STANDARD_ROOM_GROUP_VALUES,
    taskTemplateIds: [],
  } as ScanTemplateGroup);

  return retval;
};
